import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { m } from 'motion/react';
import { Sparkles, Trash2 } from 'lucide-react';

import { employeeTemplates, type CompanyEmployeeTemplate } from '../../api/organization';
import { Button } from '../../components/Button';
import { ConfirmByName } from '../../components/ConfirmByName';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { PageHeader } from '../../components/Surface';
import { SkeletonRows } from '../../components/Skeleton';
import { RISE, STAGGER } from '../../components/motion';
import { useFormat } from '../../i18n/format';
import { useT } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useCompanyId, useCompanySlug } from '../company/useCompany';

/**
 * Los roles desde los que esta empresa contrata: los que se ha escrito ella y,
 * debajo y sin acciones, los doce de `04 §9`.
 *
 * Existe porque una plantilla propia solo se veía dentro del selector de
 * contratación, que es un sitio para elegir y no para gestionar: no había forma
 * de borrar la que salió mal, ni de ver cuántas hay.
 *
 * **Borrar no toca a nadie contratado desde ella.** El perfil se copia en el
 * momento de contratar y no se referencia (`TemplateHiring`), y eso es
 * exactamente lo que hace que ofrecer el borrado sea seguro — por eso el
 * diálogo lo dice en lugar de limitarse a advertir.
 */
export function TemplatesScreen() {
  const t = useT();
  const format = useFormat();
  const companyId = useCompanyId();
  const companySlug = useCompanySlug();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<CompanyEmployeeTemplate | null>(null);

  const templates = useQuery({
    queryKey: ['employee-templates', companyId],
    queryFn: () => employeeTemplates.list(companyId),
    enabled: companyId !== '',
  });

  const remove = useMutation({
    mutationFn: (id: string) => employeeTemplates.remove(id),
    onSuccess: () => {
      setDeleting(null);
      void queryClient.invalidateQueries({ queryKey: ['employee-templates', companyId] });
    },
  });

  const rows = templates.data?.data ?? [];
  const own = rows.filter((template) => template.origin === 'company');
  const platform = rows.filter((template) => template.origin === 'platform');

  return (
    <section className="space-y-6">
      <PageHeader
        title={t('templates.title')}
        description={t('templates.intro')}
        actions={
          <Link to="/c/$companySlug/employees/templates/new" params={{ companySlug }}>
            <Button icon={<Sparkles />}>{t('templateCreator.fromTemplate')}</Button>
          </Link>
        }
      />

      {templates.error ? <ErrorBanner error={templates.error} onRetry={() => void templates.refetch()} /> : null}
      {remove.error ? <ErrorBanner error={remove.error} /> : null}

      {templates.isPending ? <SkeletonRows /> : null}

      {!templates.isPending && own.length === 0 ? (
        <EmptyState
          icon={<Sparkles />}
          title={t('templates.noneYet')}
          description={t('templates.noneYetHint')}
          action={
            <Link
              to="/c/$companySlug/employees/templates/new"
              params={{ companySlug }}
              className="text-sm font-semibold text-primary"
            >
              {t('templateCreator.fromTemplate')}
            </Link>
          }
        />
      ) : null}

      {own.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-ink">{t('templates.own')}</h2>

          <m.ul variants={STAGGER} initial="hidden" animate="shown" className="space-y-2">
            {own.map((template) => (
              <m.li
                key={template.key}
                variants={RISE}
                className="flex items-start justify-between gap-4 rounded-md border border-line bg-raised p-4"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink">{template.label}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-ink-soft">{template.description}</p>
                  <p className="mt-2 font-mono text-[0.62rem] text-ink-faint">
                    {template.key} · {template.job_title} · {template.model} · {template.reasoning_effort} ·{' '}
                    {t('employees.hire.tokens', { count: format.number(template.estimated_tokens) })}
                  </p>
                </div>

                <Button
                  variant="danger-ghost"
                  size="sm"
                  icon={<Trash2 />}
                  aria-label={t('templates.deleteLabel', { name: template.label })}
                  onClick={() => setDeleting(template)}
                >
                  {t('common.delete')}
                </Button>
              </m.li>
            ))}
          </m.ul>
        </section>
      ) : null}

      {platform.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-ink">{t('templates.platform')}</h2>
          <p className="text-xs text-ink-soft">{t('templates.platformHint')}</p>

          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {platform.map((template) => (
              <li key={template.key} className={cn('rounded-md border border-line bg-paper p-3')}>
                <p className="text-sm font-semibold text-ink">{template.label}</p>
                <p className="mt-1 text-xs leading-relaxed text-ink-soft">{template.description}</p>
                <p className="mt-2 font-mono text-[0.62rem] text-ink-faint">{template.key}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {deleting !== null ? (
        <ConfirmByName
          name={deleting.label}
          verb={t('common.delete')}
          description={t('templates.deleteConfirm')}
          busy={remove.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            if (deleting.id !== null) {
              remove.mutate(deleting.id);
            }
          }}
        />
      ) : null}
    </section>
  );
}
